import React from 'react';
import styled from 'styled-components';
import {
  Box,
  Divider,
  Checkbox,
  FormGroup,
  Typography,
  FormControlLabel
} from '@material-ui/core';

import { useWorkflowFields } from '../Workflow.hooks';
import { WorkflowTaskTypeEntity } from '../Workflow.interface';

const StyledFormGroup = styled(FormGroup)`
  padding-left: 8px;
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  padding-left: 8px;
`;

export interface FieldSelectorProps {
  fields: string[];
  type?: WorkflowTaskTypeEntity | string;
  onChange?: (fields: string[]) => void;
}

const FieldSelector: React.FC<FieldSelectorProps> = (
  props: FieldSelectorProps
) => {
  const { type, fields: selectedFields, onChange } = props;
  const { fields, labels } = useWorkflowFields(type as WorkflowTaskTypeEntity);
  const hasFields: boolean = fields.length > 0;
  const allFieldsSelected: boolean = selectedFields.length >= fields.length;
  const noFieldsSelected: boolean = selectedFields.length === 0;
  const partialFieldsSelected: boolean =
    selectedFields.length > 0 && selectedFields.length < fields.length;
  const label: string = allFieldsSelected
    ? 'Deselect All Fields'
    : noFieldsSelected || partialFieldsSelected
      ? 'Select All Fields'
      : 'Toggle Fields';

  const handleToggle = () => {
    if (allFieldsSelected) {
      // deselect all
      onChange?.([]);
    } else if (noFieldsSelected || partialFieldsSelected) {
      // select all
      onChange?.([...fields]);
    }
  };

  const handleChange = (field: string) => {
    const newFields: string[] = [...selectedFields];
    const existingIndex: number = newFields.indexOf(field);
    // remove if existing, else push
    if (existingIndex >= 0) {
      newFields.splice(existingIndex, 1);
    } else {
      newFields.push(field);
    }
    onChange?.(newFields);
  };

  return (
    <>
      <StyledFormControlLabel
        {...{ label }}
        control={
          <Checkbox
            name='toggle'
            color='primary'
            onChange={handleToggle}
            disabled={!hasFields}
            indeterminate={partialFieldsSelected}
            checked={hasFields && allFieldsSelected}
          />
        }
      />
      <Divider />
      <StyledFormGroup>
        {!hasFields && (
          <Box my={2}>
            <Typography variant='body1'>
              There are no fields for this entity.
            </Typography>
          </Box>
        )}
        {fields.map((field: string, index: number) => (
          <FormControlLabel
            key={`${field}-${index}`}
            label={labels[field as keyof typeof labels]}
            control={
              <Checkbox
                name={field}
                color='primary'
                onChange={() => handleChange(field)}
                checked={selectedFields.indexOf(field) >= 0}
              />
            }
          />
        ))}
        <Divider />
        <FormControlLabel
          label='Assign SubTasks (i.e. Approvers)'
          control={
            <Checkbox
              name='assignSubTasks'
              color='primary'
              onChange={() => handleChange('assignSubTasks')}
              checked={selectedFields.indexOf('assignSubTasks') >= 0}
            />
          }
        />
      </StyledFormGroup>
    </>
  );
};

export default FieldSelector;
